import styled from 'styled-components';

import { Tooltip } from 'opencare-crown';

import {
  BREAKPOINTS,
  colors,
  FONT_FAMILIES,
} from '../../common/styles/style-constants';

export const AdditionalBlock = styled.div`
  @media (min-width: ${BREAKPOINTS.tabletMin}px) {
    max-width: ${(props) => props.width || '600px'};
    margin: 12px auto 48px;
  }
`;

export const Title = styled.h1`
  font-family: ${FONT_FAMILIES.circular};
  font-size: ${(props) => props.fontSize || '24px'};
  font-weight: 600;
  color: ${colors.darkCharcoal};
  ${(props) => props.margin && `margin: ${props.margin};`}
  line-height: 26px;
  letter-spacing: -0.015em;
  text-align: center;

  & > .anticon {
    font-size: 18px;
    position: relative;
    top: -9px;
    padding-right: 5px;
    margin-left: -4px;
    cursor: pointer;
    color: ${colors.gray6};
    transition: 0.2s color ease-in-out;

    &:hover {
      color: ${colors.sage6};
    }
  }

  @media (min-width: ${BREAKPOINTS.tabletMin}px) {
    font-size: ${(props) => props.fontSize || '26px'};
    line-height: 30px;

    & > .anticon {
      font-size: 20px;
    }
  }
`;

export const Subtitle = styled.p`
  font-size: 14px;
  line-height: 18px;
  color: ${colors.gray8};
  text-align: center;
  max-width: ${(props) => props.maxWidth || '305px'};
  margin: ${(props) => props.margin || '0 auto'};
  letter-spacing: -0.015em;

  @media (min-width: ${BREAKPOINTS.tabletMin}px) {
    max-width: inherit;
  }
`;

export const Wrapper = styled.div`
  margin-bottom: 5px;
  background-color: ${(props) => props.backgroundColor || 'initiale'};

  @media (min-width: ${BREAKPOINTS.tabletMin}px) {
    /* margin: none is an invalid rule */
    /* margin: none; */
  }
`;

export const StyledButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-direction: ${(props) => (props.hasLogo ? 'row' : 'row-reverse')};
  cursor: pointer;
  padding: 16px 20px;
  text-align: left;
  width: 100%;
  color: ${(props) => (props.checked ? colors.sage7 : colors.darkCharcoal)};
  border-radius: 10px;
  box-shadow: 0px 1px 1px rgba(0, 0, 0, 0.1);
  background: ${(props) => (props.checked ? colors.lightSage : colors.white)};
  border: ${(props) =>
    (props.checked ? `1px solid ${colors.sage6}` : `1px solid ${colors.gray4}`)};
  transition: all 0.2s ease-in-out;

  &:hover,
  &:focus {
    color: ${colors.sage7};
    border-color: ${colors.sage6};
    outline: none !important;
  }

  &:active {
    color: ${colors.sage7};
    background: ${colors.lightSage};
    border-color: ${colors.sage6};
  }

  @media (min-width: ${BREAKPOINTS.tabletMin}px) {
    flex-direction: column;
    justify-content: center;
    width: 180px;
    height: 100%;
    text-align: center;
    padding: 25px 15px;
  }
`;

export const IconWrapper = styled.div`
  position: relative;
  display: inline-block;
  margin-left: 10px;
  width: 25px;
  height: 25px;

  & > * {
    position: absolute;
    left: 50%;
    top: 50%;
    transform: translateX(-50%) translateY(-50%);
    font-size: 18px;
  }

  @media (min-width: ${BREAKPOINTS.tabletMin}px) {
    display: block;
    text-align: center;
    margin-left: 0;
    width: auto;
    height: auto;

    & + * {
      margin-top: 10px;
    }

    & > * {
      position: initial;
      transform: none;
    }
  }
`;

export const StyledTooltip = styled(Tooltip)`
  appearance: none;
  padding: 0;
  border: none;
  background: none;
  line-height: 1;

  & > * {
    font-size: 20px;
    transition: color 0.1s ease-in-out;
  }

  &:hover > * {
    color: ${colors.sage5};
  }

  @media (min-width: ${BREAKPOINTS.tabletMin}px) {
    position: absolute;
    top: 10px;
    right: 10px;

    & > * {
      font-size: 15px;
    }
  }
`;

export const CopyWrapper = styled.span`
  flex-grow: 1;

  @media (min-width: ${BREAKPOINTS.tabletMin}px) {
    flex-grow: initial;
  }
`;

export const OptionTitle = styled.p`
  font-family: ${FONT_FAMILIES.circular};
  font-weight: 600;
  ${(props) => props.muted && `color: ${colors.slate};`}
  font-size: 15px;
  letter-spacing: -0.015em;
  line-height: 16px;
  margin: ${(props) => props.margin};
`;

export const OptionDescription = styled.p`
  font-family: ${FONT_FAMILIES.openSans};
  font-weight: 400;
  font-size: 12px;
  letter-spacing: -0.015em;
  line-height: 14px;
  margin-top: 3px;
`;

export const OptionBlock = styled.div`
  @media (min-width: ${BREAKPOINTS.tabletMin}px) {
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    align-items: stretch;
    justify-content: center;
    width: ${(props) => (props.row === 3 ? '80%' : '100%')};
    margin: 0 auto 20px;
  }
`;

// Buttons
export const BaseCircularButton = styled.button`
  appearance: none;
  border: none;
  cursor: pointer;
  min-width: ${(props) => (props.small ? 'initial' : '160px')};
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: ${FONT_FAMILIES.circular};
  font-weight: 600;
  font-size: 14px;
  letter-spacing: -0.015em;
  line-height: 20px;
  padding: 15px 20px;
  border-radius: 100px;
  transition: all 0.1s ease-in-out;

  &:disabled {
    background: ${colors.gray5};
    cursor: not-allowed;
  }

  & > .anticon {
    margin-right: 5px;
  }
`;

export const PrimaryButton = styled(BaseCircularButton)`
  color: ${colors.white};
  background: ${colors.sage6};

  &:not(:disabled) {
    &:hover,
    &:focus {
      background: ${colors.sage7};
      outline: none !important;
    }

    &:active {
      background: ${colors.sage7};
    }
  }
`;
